import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Phone, Clock, Star, Navigation, Bike } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Marquee } from "@/components/Marquee";
import { MobileActionBar } from "@/components/MobileActionBar";
import { FadeUp, MaskedLines, ChapterHeading } from "@/components/Reveal";
import { MenuSection } from "@/components/MenuSection";
import { GallerySection } from "@/components/GallerySection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RestaurantPage = () => {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [other, setOther] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  useEffect(() => {
    setR(null);
    setNotFound(false);
    axios.get(`${API}/restaurants/${slug}`)
      .then((res) => setR(res.data))
      .catch(() => setNotFound(true));
    axios.get(`${API}/restaurants`)
      .then((res) => setOther(res.data.find((x) => x.id !== slug) || null))
      .catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#121110] px-6 text-center text-[#FAF6F0]">
        <p className="font-hand text-3xl text-red-400">Hopa!</p>
        <h1 className="font-display mt-2 text-4xl font-bold">Aici nu gătește nimeni.</h1>
        <Link to="/" data-testid="notfound-home-link" className="mt-6 rounded-full bg-red-600 px-7 py-3 font-bold text-white hover:bg-red-500">
          Înapoi la casele lui Mitică
        </Link>
      </div>
    );
  }

  if (!r) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#121110]">
        <p className="font-hand animate-pulse text-3xl text-red-400">Se încălzește cuptorul...</p>
      </div>
    );
  }

  return (
    <div className="bg-[#121110] text-[#FAF6F0]">
      <Navbar restaurant={r} other={other} />

      <section ref={heroRef} data-testid="restaurant-hero" className="grain relative flex min-h-[92vh] items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={r.hero_image} alt={r.name} className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121110] via-[#121110]/60 to-[#121110]/30" />
        </motion.div>
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8">
          <motion.img
            src={r.logo}
            alt={`Logo ${r.name}`}
            initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6 h-24 w-24 rounded-3xl object-cover shadow-2xl ring-4 ring-red-600/60 sm:h-28 sm:w-28"
          />
          <span data-testid="restaurant-badge" className="inline-block rounded-full border border-red-500/60 bg-red-600/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-red-300 backdrop-blur-sm">
            {r.badge}
          </span>
          <MaskedLines
            lines={[r.name]}
            delay={0.25}
            lineClassName="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
          />
          <FadeUp delay={0.7}>
            <p className="font-hand mt-4 text-2xl text-red-300 sm:text-3xl">{r.tagline}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={r.phone_link}
                data-testid="hero-phone-cta"
                className="inline-flex items-center gap-2 rounded-full bg-red-600 px-7 py-3.5 text-base font-bold text-white transition-all hover:scale-[1.03] hover:bg-red-500"
              >
                <Phone className="h-5 w-5" /> {r.phone}
              </a>
              <button
                data-testid="hero-menu-cta"
                onClick={() => {
                  const el = document.getElementById("meniu");
                  if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -72 }) : el.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-full border border-stone-500 px-7 py-3.5 text-base font-semibold text-stone-200 transition-colors hover:border-red-500 hover:text-red-400"
              >
                Vezi meniul
              </button>
            </div>
          </FadeUp>
        </div>
      </section>

      <Marquee items={r.marquee} />

      <section id="poveste" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <ChapterHeading num="01" title="Povestea casei" />
        <div className="mt-14 space-y-20">
          {r.story.map((s, i) => (
            <div key={s.num} className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <FadeUp>
                <span className="font-display text-sm tracking-[0.35em] text-red-500">{s.num}</span>
                <h2 className="font-display mt-3 text-2xl font-bold leading-tight sm:text-4xl">{s.title}</h2>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg">{s.text}</p>
              </FadeUp>
              <FadeUp delay={0.15}>
                <div className={`overflow-hidden rounded-3xl ${i % 2 === 1 ? "rotate-1" : "-rotate-1"}`}>
                  <img
                    src={i === 0 ? r.story_image : r.story_image_2}
                    alt={s.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
              </FadeUp>
            </div>
          ))}
        </div>
      </section>

      {r.brand_quote && (
        <section className="grain relative bg-[#FAF6F0] py-16 text-stone-800 sm:py-20">
          <FadeUp className="mx-auto max-w-3xl px-5 text-center sm:px-8">
            <p data-testid="brand-quote" className="font-display text-2xl italic leading-snug sm:text-3xl">
              „{r.brand_quote}"
            </p>
            <p className="font-hand mt-3 text-2xl text-red-600">— Nea Mitică</p>
          </FadeUp>
        </section>
      )}

      <div className="grain relative bg-[#161413]">
        <MenuSection menu={r.menu} note={r.menu_note} />
      </div>

      <GallerySection gallery={r.gallery} name={r.short_name} />

      <section id="rezervari" className="grain relative bg-[#FAF6F0] py-24 text-stone-800 sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <ChapterHeading num="04" title="Rezervări & contact" />
            <FadeUp className="mt-6">
              <h2 className="font-display text-3xl font-bold sm:text-5xl">
                Ținem masa, <span className="italic text-red-600">tu adu pofta</span>
              </h2>
              <div className="mt-8 space-y-5 text-base sm:text-lg">
                <p data-testid="contact-address-text" className="flex items-start gap-3">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-red-600" /> {r.address}
                </p>
                <a data-testid="contact-phone-link" href={r.phone_link} className="flex items-center gap-3 font-semibold text-red-700 transition-colors hover:text-red-600">
                  <Phone className="h-5 w-5" /> {r.phone}
                </a>
                <p className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-red-600" /> {r.hours}
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={r.gmaps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gmaps-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#121110] px-6 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.03]"
                >
                  <Navigation className="h-4 w-4" /> Google Maps
                </a>
                <a
                  href={r.waze_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="waze-button"
                  className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-6 py-3 text-sm font-bold text-stone-700 transition-colors hover:border-red-500 hover:text-red-600"
                >
                  Waze
                </a>
              </div>
            </FadeUp>

            <div className="mt-12 space-y-4">
              {r.testimonials.map((t, i) => (
                <FadeUp key={i} delay={i * 0.1}>
                  <blockquote data-testid={`testimonial-${i}`} className="rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-sm">
                    <div className="flex gap-1 text-amber-500">
                      {Array.from({ length: t.stars }).map((_, s) => <Star key={s} className="h-4 w-4 fill-current" />)}
                    </div>
                    <p className="mt-3 text-base italic leading-relaxed text-stone-700">„{t.quote}"</p>
                    <footer className="font-hand mt-2 text-xl text-red-600">— {t.author}</footer>
                  </blockquote>
                </FadeUp>
              ))}
            </div>
          </div>

          <div className="lg:sticky lg:top-24 lg:self-start">
            <FadeUp>
              <div data-testid="phone-reservation-card" className="rounded-3xl bg-[#121110] p-8 text-[#FAF6F0] shadow-xl sm:p-10">
                <Phone className="h-9 w-9 text-red-500" />
                <h3 className="font-display mt-4 text-2xl font-bold sm:text-3xl">
                  {r.delivery_note ? "Rezervări & comenzi — doar la telefon" : "Rezervări doar la telefon"}
                </h3>
                <p className="font-hand mt-2 text-xl leading-snug text-red-300">{r.reservation_note}</p>
                <a
                  href={r.phone_link}
                  data-testid="reservation-phone-cta"
                  className="mt-6 flex items-center justify-center gap-3 rounded-full bg-red-600 py-4 text-lg font-bold text-white transition-transform hover:scale-[1.02]"
                >
                  <Phone className="h-5 w-5" /> {r.phone}
                </a>
                <p className="mt-4 text-center text-sm text-stone-400">{r.hours}</p>
              </div>
            </FadeUp>
            {r.delivery_note && (
              <FadeUp delay={0.15}>
                <div data-testid="delivery-note" className="mt-6 rounded-3xl border-2 border-dashed border-red-300 bg-white p-7 shadow-sm">
                  <div className="flex items-center gap-3">
                    <Bike className="h-6 w-6 text-red-600" />
                    <h4 className="font-display text-xl font-bold text-stone-900">Comenzi acasă</h4>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600">{r.delivery_note}</p>
                  <p className="font-hand mt-3 text-xl text-red-600">Pipera · Iancu Nicolae · Șisești</p>
                </div>
              </FadeUp>
            )}
          </div>
        </div>
      </section>

      <Footer restaurants={other ? [r, other] : [r]} />
      <MobileActionBar restaurant={r} />
    </div>
  );
};

export default RestaurantPage;
